import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Button } from "../../components/Button";
import { ExistingAccount } from "../../components/ExistingAccount/ExistingAccount";
import { useFormContext } from "../../components/Form";
import { OtpInputSection, PhoneVerificationMessage, ResendCodeSection } from "../../components/OtpComponent/components";
import { Separator } from "../../components/Separator/Separator";
import { Telephone } from "../../components/Telephone/Telephone";
import { toast } from "../../components/Toaster";
import { useCounters } from "../../hooks/useCounters/useCounters";
import { usePhoneNumberFromAv } from "../../hooks/usePhoneNumberFromAv";
import { useConfig } from "../../providers/useConfig";
import { useTranslations } from "../../providers/useTranslations";
import { validationPatterns } from "../../utils/validationPatterns";
import styles from "./OtpComponent.module.scss";
const CONSTANTS = {
    DEFAULT_TIME: 10,
    TELEPHONE_PREFIX: "editPhone",
};
const numberOfOtpInputs = 4;
const handleEnter = (cb) => (e) => {
    if (e.key === "Enter" && !e.isDefaultPrevented()) {
        cb(e);
    }
};
export const OtpComponent = memo(({ inputStyle = "", dataTestId = "otp", fieldName = (name) => name, isInputNum = false, onSendCode, onCheckPhone, isShowBorder = false, isShowSeparator = false, isOTPenabled = true, isPhoneRequired = false, isCounterEnabled = false, time = CONSTANTS.DEFAULT_TIME, onSubmitOTP, isPhoneValidatedViaSms = false, autocompleteType = "off", isSendCodeOnlyForExistingAccount = false, isAttemptError = false, resendBtnNextLine = false, attemptsNum = 0, sendCodeBtnText, className, isShowLoginButton = false, isLoginButtonDisabled = false, }) => {
    const { __ } = useTranslations();
    const [isAccountExist, setIsAccountExist] = useState(false);
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isSendingCode, setIsSendingCode] = useState(false);
    const { REGISTER_PHONE_REQUIRED, isInternationalPhoneEnabled } = useConfig();
    const { remainingTime, resetTimer, clearTimer } = useCounters(time);
    const { watch, setValue, formState: { errors }, } = useFormContext();
    const telephonePrefix = CONSTANTS.TELEPHONE_PREFIX;
    const phoneFieldName = fieldName(`${telephonePrefix}.${isInternationalPhoneEnabled ? "internationalPhone" : "phone"}`);
    const { value } = watch(`${telephonePrefix}.code`) || {};
    const phone = watch(fieldName(phoneFieldName));
    const code = watch(fieldName("phone_validation_code"));
    const telephoneFieldName = fieldName("telephone");
    const { isAutopopulated } = usePhoneNumberFromAv(fieldName(phoneFieldName));
    useEffect(() => {
        if (isInternationalPhoneEnabled && phone) {
            setValue(telephoneFieldName, phone.replace(validationPatterns.SPACES, ""));
        }
        else if (value && phone) {
            setValue(telephoneFieldName, `${value}${phone.replace(validationPatterns.SPACES, "")}`);
        }
        else {
            setValue(telephoneFieldName, "");
        }
    }, [value, phone, setValue, telephoneFieldName, isInternationalPhoneEnabled]);
    useEffect(() => {
        if (isPhoneValidatedViaSms && isCounterEnabled) {
            clearTimer();
        }
    }, [clearTimer, isPhoneValidatedViaSms, isCounterEnabled]);
    const sendCode = useCallback((event) => {
        event.preventDefault();
        setIsSendingCode(true);
        onSendCode();
        resetTimer();
        setIsSendingCode(false);
    }, [onSendCode, resetTimer]);
    const handleSendCode = useCallback(async (event) => {
        if (isCodeSent || !phone)
            return;
        try {
            const trimmedPhone = phone.replace(validationPatterns.SPACES, "");
            const formattedPhone = isInternationalPhoneEnabled ? trimmedPhone : `${value}${trimmedPhone}`;
            const { statusCode } = await onCheckPhone(formattedPhone);
            if (String(statusCode) === String(200)) {
                setIsAccountExist(!isSendCodeOnlyForExistingAccount);
                if (isSendCodeOnlyForExistingAccount) {
                    setIsCodeSent(true);
                    sendCode(event);
                }
            }
        }
        catch (err) {
            if (isSendCodeOnlyForExistingAccount) {
                toast.error(__("auth.login.phone.account_not_exist"));
            }
            else {
                setIsAccountExist(false);
                setIsCodeSent(true);
                sendCode(event);
            }
        }
    }, [
        isCodeSent,
        phone,
        isInternationalPhoneEnabled,
        value,
        onCheckPhone,
        isSendCodeOnlyForExistingAccount,
        sendCode,
        __,
    ]);
    const handleSubmitOTP = (e) => {
        if (code.length < numberOfOtpInputs)
            return;
        e.preventDefault();
        onSubmitOTP();
    };
    const isCounterTicking = isCounterEnabled && Boolean(remainingTime);
    const isTelephoneRequired = isOTPenabled || isPhoneRequired || JSON.parse(REGISTER_PHONE_REQUIRED || "false");
    const isTelephoneDisabled = isPhoneValidatedViaSms || (isCodeSent && isCounterTicking) || isAutopopulated;
    const telephoneProps = useMemo(() => ({
        fieldName: (name) => `${telephonePrefix}.${name}`,
        isRequired: isTelephoneRequired,
        isDisabled: isTelephoneDisabled,
        isInfoMessageVisible: true,
    }), [telephonePrefix, isTelephoneRequired, isTelephoneDisabled]);
    const isLoginBtnDisabled = code?.length < numberOfOtpInputs || isLoginButtonDisabled;
    return (_jsxs("div", { className: classNames(styles.otpWrapper, className), "data-testid": "otpComponent", children: [_jsx("div", { onKeyDown: handleEnter(handleSendCode), children: _jsx(Telephone, { ...telephoneProps }) }), _jsx(PhoneVerificationMessage, { isVisible: isPhoneValidatedViaSms, className: styles.phoneVerifiedAlert }), isOTPenabled && (_jsxs(_Fragment, { children: [!isPhoneValidatedViaSms && (_jsxs(_Fragment, { children: [_jsx(Button, { "data-testid": "sendCode", color: "primary", onClick: handleSendCode, disabled: !phone || isCodeSent || Boolean(Object.keys(errors)?.length), isPending: isSendingCode, children: isCodeSent
                                    ? __("profile.phone_update_page.otp.button_send_text")
                                    : sendCodeBtnText || __("profile.phone_update_page.otp.button_text") }), !isCodeSent && (_jsx("div", { className: styles.text, children: __("profile.phone_update_page.otp.verification_text") }))] })), isAccountExist && _jsx(ExistingAccount, {}), !isAccountExist && isCodeSent && !isPhoneValidatedViaSms && (_jsxs(_Fragment, { children: [isShowSeparator && _jsx(Separator, { className: styles.separator }), _jsxs("div", { className: classNames(styles.innerWrapper, { [styles.border]: isShowBorder }), onKeyDown: handleEnter(handleSubmitOTP), children: [_jsx("div", { className: styles.text, children: __("profile.phone_update_page.otp.expire_text") }), _jsx(OtpInputSection, { fieldName: fieldName, dataTestId: dataTestId, inputStyle: inputStyle, isInputNum: isInputNum, autocompleteType: autocompleteType, isAttemptError: isAttemptError, attemptsNum: attemptsNum, isDisabled: isSendCodeOnlyForExistingAccount && attemptsNum === 0 }), isShowLoginButton && (_jsx(Button, { className: styles.loginButton, color: "primary", disabled: isLoginBtnDisabled, "data-testid": "otpLoginButton", onClick: handleSubmitOTP, children: __("login.login_button_label") })), _jsx(ResendCodeSection, { isCounterTicking: isCounterTicking, remainingTime: remainingTime, isDisabled: isSendCodeOnlyForExistingAccount && attemptsNum === 0, resendBtnNextLine: resendBtnNextLine, onResend: sendCode })] })] }))] }))] }));
});
//# sourceMappingURL=OtpComponent.js.map